import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import {
  ClipboardDocumentIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'
import { MENTOR_PHONE, MENTOR_ZALO_URL } from './types'

interface ZaloJoinPanelProps {
  zaloLink?: string | null
  completedCount?: number
  totalCount?: number
  /** Compact layout for header (next to course title) */
  compact?: boolean
}

const ZaloJoinPanel = ({
  zaloLink,
  completedCount = 0,
  totalCount = 0,
  compact = false,
}: ZaloJoinPanelProps) => {
  const [qrDataUrl, setQrDataUrl] = useState('')
  const [copied, setCopied] = useState(false)

  const hasClassZalo = Boolean(zaloLink?.trim())
  const targetUrl = hasClassZalo ? zaloLink!.trim() : MENTOR_ZALO_URL

  useEffect(() => {
    let cancelled = false
    const generate = async () => {
      try {
        const dataUrl = await QRCode.toDataURL(targetUrl, {
          width: compact ? 160 : 220,
          margin: 2,
          color: { dark: '#000000', light: '#FFFFFF' },
        })
        if (!cancelled) setQrDataUrl(dataUrl)
      } catch {
        if (!cancelled) setQrDataUrl('')
      }
    }
    void generate()
    return () => {
      cancelled = true
    }
  }, [targetUrl, compact])

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* ignore */
    }
  }

  return (
    <div
      className={`bg-m2-surface-container border border-m2-outline-variant rounded-2xl shadow-sm h-full flex flex-col ${
        compact ? 'p-4' : 'p-6'
      }`}
    >
      <h3
        className={`font-bold text-m2-on-surface ${
          compact ? 'text-base mb-1' : 'text-xl mb-2'
        }`}
      >
        {hasClassZalo ? 'Tham gia nhóm Zalo lớp' : 'Liên hệ mentor để add nhóm'}
      </h3>
      {!compact && (
        <p className="text-sm text-m2-on-surface-variant mb-5">
          {hasClassZalo
            ? 'Quét mã QR hoặc mở link để vào nhóm Zalo lớp học.'
            : `Lớp chưa có link Zalo. Liên hệ mentor ${MENTOR_PHONE} để được add nhóm.`}
        </p>
      )}
      {compact && !hasClassZalo && (
        <p className="text-xs text-m2-on-surface-variant mb-3">
          SĐT mentor: {MENTOR_PHONE}
        </p>
      )}

      <div className="flex flex-col items-center flex-1">
        <div className="bg-white rounded-xl p-2.5 shadow-sm border border-m2-outline-variant">
          {qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt="QR Zalo"
              className={compact ? 'w-36 h-36' : 'w-44 h-44 sm:w-52 sm:h-52'}
            />
          ) : (
            <div
              className={`flex items-center justify-center text-m2-on-surface-variant text-sm ${
                compact ? 'w-36 h-36' : 'w-44 h-44 sm:w-52 sm:h-52'
              }`}
            >
              Đang tạo QR...
            </div>
          )}
        </div>

        {!hasClassZalo && !compact && (
          <p className="mt-3 text-lg font-bold text-m2-on-surface tracking-wide">
            {MENTOR_PHONE}
          </p>
        )}

        <div className={`flex flex-wrap justify-center gap-2 w-full ${compact ? 'mt-3' : 'mt-4'}`}>
          <a
            href={targetUrl}
            target="_blank"
            rel="noreferrer"
            className={`btn-primary-soft flex-1 min-w-[110px] ${
              compact ? 'px-3 text-sm' : 'h-11 px-4'
            }`}
          >
            <ChatBubbleLeftRightIcon className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
            {hasClassZalo ? 'Mở Zalo' : 'Nhắn Zalo'}
          </a>
          <button
            type="button"
            onClick={() => copyText(hasClassZalo ? targetUrl : MENTOR_PHONE)}
            className={`inline-flex items-center justify-center gap-1.5 flex-1 min-w-[110px] btn-secondary ${
              compact ? 'px-3 text-sm' : 'h-11 px-4'
            }`}
          >
            <ClipboardDocumentIcon className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
            {copied ? 'Đã copy' : hasClassZalo ? 'Copy link' : 'Copy SĐT'}
          </button>
        </div>

        {!compact && totalCount > 0 && (
          <p className="mt-5 text-sm text-m2-on-surface-variant">
            Đã học <span className="font-semibold text-m2-on-surface">{completedCount}</span>
            /{totalCount} buổi
          </p>
        )}
      </div>
    </div>
  )
}

export default ZaloJoinPanel
